import React from "react";
import { StoreState } from "../reducers";
import * as methods from "../reducers/methods";
import { connect } from "react-redux";
import { registerAnimation } from "../actions";
import { CSSTransition } from "react-transition-group";
import { animationTimeouts } from "./AnimationConfig";
import { AnimationStatus } from "../actions/animation/types";

interface OwnProps {
  position: number | string | undefined;
  animationEnter?: string | undefined;
  animationLeave?: string | undefined;
}

interface Props extends OwnProps {
  registerAnimation: typeof registerAnimation;
  status: AnimationStatus;
  index: number;
  enterOffset: number;
  exitOffset: number;
  startTime: number;
  animationEnter?: string | undefined;
  animationLeave?: string | undefined;
}

interface AnimationClassNames {
  enter?: string;
  enterActive?: string;
  enterDone?: string;
  exit?: string;
  exitActive?: string;
  exitDone?: string;
  appear?: string;
  appearActive?: string;
}

export type AnimationName =
  | "appear"
  | "fly-left"
  | "fly-right"
  | "fly-top"
  | "fly-bottom";

interface State {
  in: boolean;
}

export class AnimationComponent extends React.Component<Props, State> {
  timeout: {
    enter: number;
    exit: number;
  };
  hasEnterAnimation: boolean;
  hasExitAnimation: boolean;

  constructor(props: Props) {
    super(props);

    this.timeout = {
      enter: 0,
      exit: 0,
    };

    const { animationEnter, animationLeave } = this.props;

    if (animationEnter && animationTimeouts[animationEnter]) {
      this.timeout.enter = animationTimeouts[animationEnter].enter;
      this.hasEnterAnimation = true;
    } else {
      this.hasEnterAnimation = false;
    }

    if (animationLeave && animationTimeouts[animationLeave]) {
      this.timeout.exit = animationTimeouts[animationLeave].exit;
      this.hasExitAnimation = true;
    } else {
      this.hasExitAnimation = false;
    }

    this.state = {
      in: true,
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.status !== this.props.status &&
      this.props.status === "leaving"
    ) {
      this.exit();
    }

    // this lets the builder show an animation after a selection has been changed
    if (this.props.animationEnter !== prevProps.animationEnter) {
      if (this.hasEnterAnimation) {
        this.props.registerAnimation({
          timeout: this.timeout.enter,
          index: this.props.index,
          position: "enter",
        });
        this.setState({ in: false });
        this.scheduleEnter();
      }
    }
  }

  enter = () => {
    this.setState({ in: true });
  };

  exit = () => {
    if (this.props.animationLeave) {
      setTimeout(() => this.setState({ in: false }), this.props.exitOffset);
    }
  };

  scheduleEnter = () => {
    const now = new Date().getTime();
    const startDelta = this.props.enterOffset + this.props.startTime - now;

    setTimeout(this.enter, startDelta < 0 ? 0 : startDelta);
  };

  componentDidMount() {
    if (this.hasEnterAnimation) {
      this.props.registerAnimation({
        timeout: this.timeout.enter,
        index: this.props.index,
        position: "enter",
      });
      this.setState({ in: false });
    }

    if (this.hasExitAnimation) {
      this.props.registerAnimation({
        timeout: this.timeout.exit,
        index: this.props.index,
        position: "leave",
      });
    }

    setTimeout(this.scheduleEnter);
  }

  render() {
    const enter = this.props.status === "entering";
    const exit = this.props.status === "leaving";

    const { animationEnter, animationLeave, children } = this.props;

    if (this.hasEnterAnimation || this.hasExitAnimation) {
      const classNames: AnimationClassNames = {};

      // If an enter animation exist
      if (this.hasEnterAnimation) {
        classNames.enter = `${animationEnter}-enter`;
        classNames.enterActive = `${animationEnter}-enter-active`;
        classNames.enterDone = `${animationEnter}-enter-done`;
      }

      // If an exit animation exist
      if (this.hasExitAnimation) {
        classNames.exit = `${animationLeave}-exit`;
        classNames.exitActive = `${animationLeave}-exit-active`;
        classNames.exitDone = `${animationLeave}-exit-done`;
      } else {
        classNames.exitDone = `animation-invisible`;
      }

      return (
        <CSSTransition
          in={this.state.in}
          enter={enter}
          appear={true}
          exit={exit}
          classNames={classNames}
          timeout={this.timeout}
        >
          {() => children}
        </CSSTransition>
      );
    } else {
      return children;
    }
  }
}

const mapStateToProps = (state: StoreState, ownProps: OwnProps) => {
  const index =
    typeof (ownProps.position || 0) === "string"
      ? parseInt(ownProps.position as string, 10)
      : (ownProps.position as number);

  return {
    index,
    status: methods.getAnimationStatus(state),
    startTime: methods.getAnimationStartTime(state),
    enterOffset: methods.getEnterOffset(state, index),
    exitOffset: methods.getExitOffset(state, index),
  };
};

export const Animation = connect(mapStateToProps, { registerAnimation })(
  AnimationComponent
);
